import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { formatTimestamp } from "@/utils/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

// Clean, dedicated place to read/add a project's General Project Notes.
// Backed by the existing communication_items system (via /general-notes) — no
// separate storage. Deep-linked from the board Notes preview (#general-project-notes).
export const GeneralProjectNotesPanel = ({ canAdd, onChanged, projectId }) => {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/general-notes`);
      setNotes(response.data.items || []);
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const addNote = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) {
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/general-notes`, {
        notes: text,
        additional_notes: "",
      });
      setDraft("");
      toast.success("General Project Note added.");
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add project note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="command-panel general-project-notes-panel"
      data-testid="general-project-notes-panel"
      id="general-project-notes"
    >
      <div className="command-panel-heading">
        <div>
          <span className="operations-kicker">Project notes</span>
          <h2>General Project Notes</h2>
        </div>
      </div>

      {canAdd && (
        <form
          className="command-field-note-form"
          data-testid="general-note-form"
          onSubmit={addNote}
        >
          <textarea
            data-testid="general-note-input"
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Add a General Project Note"
            value={draft}
          />
          <button
            data-testid="general-note-save"
            disabled={!draft.trim() || saving}
            type="submit"
          >
            <MessageSquarePlus size={14} />
            {saving ? "Adding…" : "Add General Project Note"}
          </button>
        </form>
      )}

      <div className="command-communication-list" data-testid="general-notes-list">
        {loading ? (
          <p className="command-empty-state">Loading notes…</p>
        ) : notes.length ? (
          notes.map((note) => (
            <article data-testid={`general-note-${note.id}`} key={note.id}>
              <strong data-testid={`general-note-body-${note.id}`}>
                {note.original_note || note.notes || "Project note"}
              </strong>
              {(note.additional_note_entries || []).map((entry) => (
                <p data-testid={`general-note-entry-${entry.id}`} key={entry.id}>
                  {entry.message}
                  {entry.author_name_snapshot ? ` · ${entry.author_name_snapshot}` : ""}
                </p>
              ))}
              <small data-testid={`general-note-meta-${note.id}`}>
                {note.created_by_name_snapshot || "Unknown author"} ·{" "}
                {note.created_at ? formatTimestamp(note.created_at) : "—"}
              </small>
            </article>
          ))
        ) : (
          <p className="command-empty-state" data-testid="general-notes-empty">
            No General Project Notes yet.
          </p>
        )}
      </div>
    </section>
  );
};
